/**
 * Audio sources — the station dial.
 *
 * Every station carries a display name (shown by the on-air indicator)
 * and a coarse region tag used to weight rotation toward stations near
 * the listener (see guessUserRegion — timezone-based, no network call,
 * no IP lookup, nothing leaves the browser).
 *
 * Regions: 'us' | 'latam' | 'europe' | 'asia' | 'global'
 *
 * Local files (./sounds/*.mp3) reference /public/sounds/. The AudioManager
 * auto-skips any source that errors out, so a dead stream won't break
 * playback — it just gets blacklisted for the session.
 *
 * All streams verified reachable 2026-07-31.
 */

export const STATIONS = [
  // ── West-coast / US ─────────────────────────────────────────
  { url: 'https://ice1.somafm.com/groovesalad-128-mp3', name: 'SOMA FM · GROOVE SALAD', region: 'us' },
  { url: 'https://ice1.somafm.com/u80s-128-mp3', name: 'SOMA FM · UNDERGROUND 80s', region: 'us' },
  { url: 'https://ice1.somafm.com/metal-128-mp3', name: 'SOMA FM · METAL DETECTOR', region: 'us' },
  { url: 'https://ice1.somafm.com/doomed-128-mp3', name: 'SOMA FM · DOOMED', region: 'us' },
  { url: 'https://ice1.somafm.com/vaporwaves-128-mp3', name: 'SOMA FM · VAPORWAVES', region: 'us' },
  { url: 'https://ice1.somafm.com/dronezone-128-mp3', name: 'SOMA FM · DRONE ZONE', region: 'us' },
  { url: 'https://listen.xray.fm/stream', name: 'XRAY.FM · PORTLAND', region: 'us' },
  { url: 'https://kexp-mp3-128.streamguys1.com/kexp128.mp3', name: 'KEXP · SEATTLE', region: 'us' },
  { url: 'https://stream0.wfmu.org/freeform-128k', name: 'WFMU · FREEFORM', region: 'us' },
  { url: 'https://stream.radioparadise.com/mp3-128', name: 'RADIO PARADISE · MAIN', region: 'us' },
  { url: 'https://stream.radioparadise.com/rock-128', name: 'RADIO PARADISE · ROCK', region: 'us' },
  { url: 'https://stream.radioparadise.com/mellow-128', name: 'RADIO PARADISE · MELLOW', region: 'us' },
  { url: 'https://streamer.radio.co/scad0cc067/listen', name: 'SCAD RADIO · ATLANTA', region: 'us' },
  { url: 'https://live.kboo.fm:8443/high', name: 'KBOO · PORTLAND', region: 'us' },
  { url: 'https://listen.freeformportland.org/stream', name: 'FREEFORM PORTLAND', region: 'us' },
  { url: 'https://streams.kqed.org/kqedradio', name: 'KQED · SAN FRANCISCO', region: 'us' },
  { url: 'https://dublab.out.airtime.pro/dublab_a', name: 'DUBLAB · LOS ANGELES', region: 'us' },
  { url: 'https://wxpnhi.xpn.org/xpnhi', name: 'WXPN · PHILADELPHIA', region: 'us' },
  { url: 'https://onair.wfuv.org/onair-hi', name: 'WFUV · NEW YORK', region: 'us' },
  { url: 'https://current.stream.publicradio.org/kcmp.mp3', name: 'THE CURRENT · MINNEAPOLIS', region: 'us' },
  { url: 'https://ice1.somafm.com/indiepop-128-mp3', name: 'SOMA FM · INDIE POP ROCKS', region: 'us' },
  { url: 'https://ice1.somafm.com/bagel-128-mp3', name: 'SOMA FM · BAGEL RADIO', region: 'us' },
  { url: 'https://ice1.somafm.com/sf1033-128-mp3', name: 'SOMA FM · SF 10-33 SCANNER', region: 'us' },
  { url: 'https://ice1.somafm.com/missioncontrol-128-mp3', name: 'SOMA FM · MISSION CONTROL', region: 'us' },
  { url: 'https://ice1.somafm.com/deepspaceone-128-mp3', name: 'SOMA FM · DEEP SPACE ONE', region: 'us' },
  { url: 'https://ice1.somafm.com/secretagent-128-mp3', name: 'SOMA FM · SECRET AGENT', region: 'us' },
  { url: 'https://ice1.somafm.com/7soul-128-mp3', name: 'SOMA FM · SEVEN INCH SOUL', region: 'us' },
  { url: 'https://ice1.somafm.com/bootliquor-128-mp3', name: 'SOMA FM · BOOT LIQUOR', region: 'us' },
  // Unverified from the sandbox (host blocked curl) — try in a browser:
  // { url: 'https://kcrw.streamguys1.com/kcrw_192k_mp3_e24', name: 'KCRW · ECLECTIC 24', region: 'us' },
  // { url: 'https://kutx-ice.streamguys1.com/kutx-free.aac', name: 'KUTX · AUSTIN', region: 'us' },
  // { url: 'https://hollowearthradio.out.airtime.pro/hollowearthradio_a', name: 'HOLLOW EARTH · SEATTLE', region: 'us' },

  // ── News desks ──────────────────────────────────────────────
  { url: 'https://npr-ice.streamguys1.com/live.mp3', name: 'NPR · NATIONAL NEWS', region: 'us' },
  { url: 'https://fm939.wnyc.org/wnycfm', name: 'WNYC · NEW YORK', region: 'us' },
  { url: 'https://stream.live.vc.bbcmedia.co.uk/bbc_world_service', name: 'BBC WORLD SERVICE', region: 'global' },
  { url: 'https://icecast.radiofrance.fr/franceinfo-midfi.mp3', name: 'FRANCE INFO', region: 'europe' },
  { url: 'https://live-radio01.mediahubaustralia.com/PBW/mp3/', name: 'ABC NEWS · AUSTRALIA', region: 'asia' },

  // ── Latin America ───────────────────────────────────────────
  { url: 'https://media.radios.ar:1110/', name: 'RADIO AR · 1110', region: 'latam' },
  { url: 'https://play12.tikast.com/proxy/tangomedellinradio?mp=/stream', name: 'TANGO MEDELLÍN', region: 'latam' },
  { url: 'https://emisoras.colombiacrossover.com/proxy/colurbana?mp=/stream', name: 'COLOMBIA URBANA', region: 'latam' },
  { url: 'https://server.ohradio.com.ar/8108/stream', name: 'OH RADIO · BUENOS AIRES', region: 'latam' },
  { url: 'https://icecast.teveo.cu/Nbtz7HT3', name: 'TEVEO · CUBA', region: 'latam' },
  { url: 'https://server.streamcasthd.com/8192/stream', name: 'STREAMCAST · 8192', region: 'latam' },

  // ── Europe ──────────────────────────────────────────────────
  { url: 'https://stream-relay-geo.ntslive.net/stream', name: 'NTS · LONDON 1', region: 'europe' },
  { url: 'https://stream-relay-geo.ntslive.net/stream2', name: 'NTS · LONDON 2', region: 'europe' },
  { url: 'https://streaming.brol.tech/jamendolounge', name: 'JAMENDO LOUNGE', region: 'europe' },
  { url: 'https://usa17.fastcast4u.com/proxy/kievradio?mp=/1', name: 'RADIO KYIV', region: 'europe' },
  { url: 'https://icecast.radiofrance.fr/fip-midfi.mp3', name: 'FIP · PARIS', region: 'europe' },
  { url: 'https://novazz.ice.infomaniak.ch/novazz-128.mp3', name: 'RADIO NOVA · PARIS', region: 'europe' },
  { url: 'https://admin.stream.rinse.fm/proxy/rinse_uk/stream', name: 'RINSE FM · LONDON', region: 'europe' },
  { url: 'https://doyouworld.out.airtime.pro/doyouworld_a', name: 'DO!!YOU!!! · LONDON', region: 'europe' },
  { url: 'https://kioskradiobxl.out.airtime.pro/kioskradiobxl_a', name: 'KIOSK RADIO · BRUSSELS', region: 'europe' },
  { url: 'https://cashmereradio.out.airtime.pro/cashmereradio_a', name: 'CASHMERE RADIO · BERLIN', region: 'europe' },
  // { url: 'https://noodsradio.out.airtime.pro/noodsradio_a', name: 'NOODS · BRISTOL', region: 'europe' },
  // { url: 'https://refugeworldwide.out.airtime.pro/refugeworldwide_a', name: 'REFUGE WORLDWIDE · BERLIN', region: 'europe' },

  // ── Asia / Pacific ──────────────────────────────────────────
  { url: 'https://xcite.radyoph.com/listen/pines/radio', name: 'RADIO PINES · PH', region: 'asia' },
  { url: 'https://live-radio01.mediahubaustralia.com/2TJW/mp3/', name: 'TRIPLE J · AUSTRALIA', region: 'asia' },
  // { url: 'https://radionz-ice.streamguys1.com/national.mp3', name: 'RNZ · NATIONAL', region: 'asia' },

  // ── Global / unplaceable signals ────────────────────────────
  { url: 'https://stream.radioparadise.com/global-128', name: 'RADIO PARADISE · GLOBAL', region: 'global' },
  { url: 'https://stream.nightride.fm/nightride.mp3', name: 'NIGHTRIDE FM · SYNTH', region: 'global' },
  { url: 'https://stream.nightride.fm/chillsynth.mp3', name: 'NIGHTRIDE FM · CHILLSYNTH', region: 'global' },
  { url: 'https://das-edge17-live365-dal02.cdnstream.com/a74343', name: 'LIVE365 · SIGNAL 74343', region: 'global' },
  { url: 'https://streamer.radio.co/scfb5749d1/listen', name: 'PIRATE SIGNAL · FB5', region: 'global' },
  { url: 'https://usa8.fastcast4u.com/proxy/shramsay?mp=/1', name: 'PIRATE SIGNAL · SHR', region: 'global' },
  { url: 'https://n0a.radiojar.com/3wg5hpdkfkeuv?rj-ttl=5&rj-tok=AAABko1LfAwA6RzCvVUzzMOLK', name: 'MYSTERY DIAL · 3WG5', region: 'global' },
  { url: 'https://ais-sa3.cdnstream1.com/2440_128.aac', name: 'CDN SIGNAL · 2440', region: 'global' },

  // ── Local tapes ─────────────────────────────────────────────
  // { url: './sounds/coffin.mp3', name: 'TAPE DECK · COFFIN', region: 'global' },
  // { url: './sounds/death-highway.mp3', name: 'TAPE DECK · DEATH HIGHWAY', region: 'global' },
  // { url: './sounds/funhouse.mp3', name: 'TAPE DECK · FUNHOUSE', region: 'global' },
  // { url: './sounds/adventure.mp3', name: 'TAPE DECK · ADVENTURE', region: 'global' },
];

const BY_URL = new Map(STATIONS.map((s) => [s.url, s]));

/** Flat pool of every station URL (AudioManager's currency is the URL). */
export const AUDIO_POOL = STATIONS.map((s) => s.url);

/** Display name for a source URL. */
export function stationName(url) {
  return BY_URL.get(url)?.name || 'UNKNOWN SIGNAL';
}

/** Region tag for a source URL. */
export function stationRegion(url) {
  return BY_URL.get(url)?.region || 'global';
}

/**
 * Coarse listener region from the browser's IANA timezone.
 * Zero network calls, zero permissions, nothing leaves the browser —
 * good enough to bias the dial toward nearby stations.
 */
export function guessUserRegion() {
  let tz = '';
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return 'global';
  }

  const LATAM = /America\/(Argentina|Bogota|Montevideo|Santiago|Lima|Caracas|La_Paz|Asuncion|Havana|Mexico_City|Cancun|Monterrey|Guatemala|Panama|Costa_Rica|Sao_Paulo|Bahia|Fortaleza|Manaus|Recife)/;
  if (LATAM.test(tz)) return 'latam';
  if (tz.startsWith('America/') || tz.startsWith('US/') || tz.startsWith('Canada/')) return 'us';
  if (tz.startsWith('Europe/') || tz.startsWith('Africa/')) return 'europe';
  if (tz.startsWith('Asia/') || tz.startsWith('Australia/') || tz.startsWith('Pacific/') || tz.startsWith('Indian/')) return 'asia';
  return 'global';
}
